/**
 * McpDocsPage — MCP Server documentation for AI Traffic Light for Code.
 * Route: /docs/mcp
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Server, Terminal, Key, Wrench, MessageSquare } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';


// ── Shared primitives ──────────────────────────────────────────────────────────

function SectionHeading({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <h2 className="flex items-center gap-2 text-xl font-semibold text-white mb-4 mt-10 first:mt-0 scroll-mt-24">
      <Icon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-zinc-100 mb-2 mt-6">{children}</h3>;
}

function Prose({ children }: { children: React.ReactNode }) {
  return <p className="text-zinc-400 leading-relaxed mb-4">{children}</p>;
}

function CodeBlock({ lang, children }: { lang?: string; children: string }) {
  return (
    <div className="rounded-lg border border-zinc-700 overflow-hidden mb-4">
      {lang && (
        <div className="px-4 py-1.5 bg-zinc-800 border-b border-zinc-700 text-xs text-zinc-500 font-mono">
          {lang}
        </div>
      )}
      <pre className="bg-zinc-800/60 p-4 overflow-x-auto">
        <code className="text-zinc-200 font-mono text-sm leading-relaxed">{children}</code>
      </pre>
    </div>
  );
}

function Pill({ children, color = 'zinc' }: { children: React.ReactNode; color?: string }) {
  const classes: Record<string, string> = {
    zinc: 'bg-zinc-700 text-zinc-300',
    emerald: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${classes[color] ?? classes.zinc}`}>
      {children}
    </span>
  );
}

// ── MCP config JSON ────────────────────────────────────────────────────────────

const MCP_CONFIG_HTTP = `{
  "mcpServers": {
    "guardrail": {
      "url": "https://mcp.code.aitrafficlight.com/mcp",
      "transport": "streamable-http"
    }
  }
}`;

const MCP_CONFIG_CLAUDE_CODE = `claude mcp add guardrail \\
  --transport http \\
  https://mcp.code.aitrafficlight.com/mcp`;

// ── Tool list ──────────────────────────────────────────────────────────────────

const TOOLS = [
  {
    name: 'guardrail_risk_report',
    desc: 'Run a comprehensive security risk report on a GitHub repository URL or local path. Returns an overall risk score with AIVSS, CVSS, and AARS metrics plus per-file findings.',
  },
  {
    name: 'guardrail_code_risk_scan',
    desc: 'Scan one or more code files for security vulnerabilities, unsafe patterns, and coding anti-patterns.',
  },
  {
    name: 'guardrail_scan_secrets',
    desc: 'Detect hardcoded secrets, API keys, passwords, tokens, and credentials in your codebase.',
  },
  {
    name: 'guardrail_generate_sbom',
    desc: 'Generate a Software Bill of Materials (SBOM) listing all dependencies, licences, and versions.',
  },
  {
    name: 'guardrail_check_license',
    desc: 'Check licence compliance across your dependencies and flag permissive, copyleft, or incompatible licences.',
  },
  {
    name: 'guardrail_compliance_check',
    desc: 'Evaluate your codebase against compliance frameworks such as OWASP Top 10, NIST, SOC 2, and ISO 27001.',
  },
  {
    name: 'guardrail_scan_dependencies',
    desc: 'Audit third-party dependencies for known CVEs and supply-chain vulnerabilities.',
  },
  {
    name: 'guardrail_scan_prompts',
    desc: 'Analyse AI prompt templates for injection risks, jailbreak vectors, and data exfiltration patterns.',
  },
  {
    name: 'guardrail_ml_security_report',
    desc: 'Perform an ML/AI security assessment covering model integrity, training data risks, inference attacks, and deployment posture.',
  },
];

// ── Example prompts ────────────────────────────────────────────────────────────

const EXAMPLES = [
  {
    prompt: 'Scan https://github.com/org/repo for security risks',
    tool: 'guardrail_risk_report',
    note: 'Triggers a full risk report with AIVSS, CVSS, and AARS scores.',
  },
  {
    prompt: 'Check for secrets in my current project',
    tool: 'guardrail_scan_secrets',
    note: 'Scans all files in context for exposed credentials.',
  },
  {
    prompt: 'Generate an SBOM for my dependencies',
    tool: 'guardrail_generate_sbom',
    note: 'Produces a structured bill of materials you can export.',
  },
  {
    prompt: 'Check my dependencies for known vulnerabilities',
    tool: 'guardrail_scan_dependencies',
    note: 'Cross-references your package manifest against CVE databases.',
  },
  {
    prompt: 'Is my codebase OWASP Top 10 compliant?',
    tool: 'guardrail_compliance_check',
    note: 'Runs a compliance crosswalk against the selected framework.',
  },
  {
    prompt: 'Assess the ML security posture of this AI application',
    tool: 'guardrail_ml_security_report',
    note: 'Evaluates AI-specific risks including prompt injection and model tampering.',
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function McpDocsPage() {
  useDocumentTitle('MCP Server');
  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] flex flex-col">
      <AppHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Back link */}
        <Link
          to="/docs"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Docs
        </Link>

        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
              <Server className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">
                MCP Server
              </h1>
              <p className="text-zinc-500 text-sm">AI Traffic Light for Code</p>
            </div>
          </div>
          <p className="text-zinc-400 leading-relaxed">
            Connect AI assistants like Claude directly to your security scanning tools via the
            Model Context Protocol.
          </p>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6 md:p-8 space-y-2">

          {/* What is MCP */}
          <SectionHeading icon={Server}>What is MCP?</SectionHeading>
          <Prose>
            The <span className="text-white font-medium">Model Context Protocol (MCP)</span> is an
            open standard that lets AI assistants call external tools and services through a
            structured interface. Instead of copying and pasting code into a chat window, you
            describe what you want in plain language and the AI invokes the right tool automatically.
          </Prose>
          <Prose>
            AI Traffic Light runs a hosted MCP server at{' '}
            <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">mcp.code.aitrafficlight.com</code>.
            Connect any MCP-compatible client — Claude Code, Cursor, VS Code Copilot, Windsurf, or
            others — to invoke security scans, generate SBOMs, detect secrets, and run compliance
            checks directly from your AI workflow. No npm install required.
          </Prose>

          {/* Connection */}
          <SectionHeading icon={Terminal}>Connect Your AI Client</SectionHeading>
          <Prose>
            The MCP server uses <span className="text-white font-medium">Streamable HTTP</span> transport —
            your AI client connects directly to the hosted server over HTTPS. Nothing to install locally.
          </Prose>

          <SubHeading>Claude Code</SubHeading>
          <Prose>
            Register the MCP server from your terminal:
          </Prose>
          <CodeBlock lang="shell">{MCP_CONFIG_CLAUDE_CODE}</CodeBlock>
          <Prose>
            The server is immediately available in your Claude Code sessions.
          </Prose>

          <SubHeading>Cursor</SubHeading>
          <Prose>
            Create or edit <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">.cursor/mcp.json</code>{' '}
            in your project root (or <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">~/.cursor/mcp.json</code>{' '}
            for a global config) and add:
          </Prose>
          <CodeBlock lang="json">{MCP_CONFIG_HTTP}</CodeBlock>
          <Prose>
            Reload Cursor — you should see "guardrail" appear in the available tools.
          </Prose>

          <SubHeading>VS Code (GitHub Copilot)</SubHeading>
          <Prose>
            VS Code with the GitHub Copilot extension supports MCP via{' '}
            <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">.vscode/mcp.json</code>.
            Add the same JSON config above and enable MCP in your Copilot settings.
          </Prose>

          <SubHeading>Claude Desktop</SubHeading>
          <Prose>
            Open or create the config file at:
          </Prose>
          <CodeBlock lang="path">{`~/Library/Application Support/Claude/claude_desktop_config.json`}</CodeBlock>
          <Prose>
            Add the JSON config above (merge with any existing <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">mcpServers</code> object),
            then restart Claude Desktop. You should see "guardrail" appear in the tool list.
          </Prose>

          <SubHeading>Other MCP Clients</SubHeading>
          <Prose>
            Any MCP client that supports <span className="text-white font-medium">Streamable HTTP</span> transport
            can connect using the server URL:
          </Prose>
          <CodeBlock lang="text">{`https://mcp.code.aitrafficlight.com/mcp`}</CodeBlock>

          {/* Authentication */}
          <SectionHeading icon={Key}>Authentication</SectionHeading>
          <Prose>
            The MCP server authenticates via your AI Traffic Light account. When you invoke a tool
            for the first time, you'll be prompted to sign in through a browser-based flow at{' '}
            <code className="text-zinc-300 bg-zinc-800 px-1.5 py-0.5 rounded text-sm">
              auth.code.aitrafficlight.com
            </code>
            . Your session is stored locally and reused automatically.
          </Prose>

          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 mb-4">
            <p className="text-blue-300 text-sm font-medium mb-1">Token refresh</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Tokens are valid for 30 days and refresh automatically. If a tool call fails with an
              auth error, re-authenticate through the browser flow when prompted by your client.
            </p>
          </div>

          {/* Available tools */}
          <SectionHeading icon={Wrench}>Available Tools</SectionHeading>
          <Prose>
            The server exposes 67 tools in total. The key security scanning tools are listed below.
          </Prose>
          <div className="space-y-3 mb-4">
            {TOOLS.map(({ name, desc }) => (
              <div
                key={name}
                className="rounded-lg border border-zinc-800 bg-zinc-800/30 p-4 hover:border-zinc-700 transition-colors"
              >
                <code className="text-emerald-400 text-sm font-mono font-semibold">{name}</code>
                <p className="text-zinc-400 text-sm leading-relaxed mt-1.5">{desc}</p>
              </div>
            ))}
          </div>
          <Prose>
            Additional tools cover AIBOM generation, dependency graph analysis, prompt library
            scanning, and more. All tools respect your subscription tier limits.
          </Prose>

          {/* Example usage */}
          <SectionHeading icon={MessageSquare}>Example Usage</SectionHeading>
          <Prose>
            Once connected, describe what you need in plain language. Your AI client selects and
            invokes the right scanning tool automatically:
          </Prose>
          <div className="space-y-4">
            {EXAMPLES.map(({ prompt, tool, note }) => (
              <div key={prompt} className="rounded-lg border border-zinc-800 bg-zinc-800/20 overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-800 flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <p className="text-white text-sm font-medium">"{prompt}"</p>
                </div>
                <div className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Wrench className="w-3.5 h-3.5" />
                    Calls:
                  </div>
                  <Pill color="emerald">{tool}</Pill>
                  <span className="hidden sm:block text-zinc-700">·</span>
                  <p className="text-zinc-500 text-xs">{note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="mt-10 rounded-lg border border-zinc-700 bg-zinc-800/30 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-white text-sm font-semibold mb-0.5">Need MCP access?</p>
              <p className="text-zinc-400 text-sm">
                MCP server access is available on Personal, Pro, Team, and Enterprise plans.
              </p>
            </div>
            <Link
              to="/pricing"
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
            >
              View pricing
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
